import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import Popup from '../Common/Popup';

/**
 * The `TransactionStatusFilter` function is a React component that renders a list of checkboxes for
 * selecting transaction statuses.
 * @returns a JSX element with a button that opens a popup modal for selecting transaction statuses.
 */
function TransactionStatusFilter({ TransactionStatus, setTransactionStatus, customPathname, label = "Transaction Status", isPopupOpen, setIsPopupOpen }) {
    const { refineSearchFilters } = useContext(PeFilterContext);
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;
   
    /* The `useEffect` hook is used to perform side effects in a React component. In this case, the
    `useEffect` hook is used to update the `TransactionStatus` array with the `isChecked` property
    based on the `refineSearchFilters` and `customPathname` values. */
    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.transactionStatus?.map(value => value.id) ?? [];
        // If no filters are set, select all by default
        if (industryIds.length === 0) {
            setTransactionStatus(TransactionStatus?.map(industry => ({ ...industry, isChecked: true })));
        } else {
            setTransactionStatus(TransactionStatus?.map(industry => ({ ...industry, isChecked: industryIds.includes(industry.id) })));
        }
    }, [customPathname, refineSearchFilters, setTransactionStatus]);


    /**
     * The handleChange function updates the isChecked property of a transaction in the
     * TransactionStatus array based on the provided id.
     * @param id - The `id` parameter is the unique identifier of the transaction that needs to be
     * updated.
     */
    const handleChange = (id) => {
        const updatedTransactionStatus = TransactionStatus.map((transaction) => {
            if (transaction.id === id) {
                return {
                    ...transaction,
                    isChecked: !transaction.isChecked,
                };
            }
            return transaction;
        });
        setTransactionStatus(updatedTransactionStatus);
    };

    const selectedTransactionLength = TransactionStatus?.filter(option => option.isChecked)?.length ?? 0;

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {TransactionStatus.slice(0,2).map(item => item.name).join(', ')}</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedTransactionLength > 0 && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selectedTransactionLength}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-4">
                        <h3 className="text-sm font-medium mb-3 text-gray-700">{label}</h3>
                        <div className="flex flex-col gap-3">
                            {TransactionStatus?.length > 0 &&
                                TransactionStatus.map((transaction, i) => {
                                    return (
                                        <div className="flex items-center" key={i}>
                                            <input 
                                                id={`transaction-${transaction.id}`} 
                                                type="checkbox" 
                                                onChange={(e) => handleChange(transaction.id)}
                                                className="cursor-pointer mr-2 w-4 h-4"
                                                checked={transaction.isChecked} 
                                            />
                                            <label 
                                                htmlFor={`transaction-${transaction.id}`} 
                                                className="text-sm cursor-pointer whitespace-nowrap"
                                            >
                                                {transaction.name}
                                            </label>
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default TransactionStatusFilter;